import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

/**
 * CRUD for square_product_mappings.
 */
export function useSquareProductMappings() {
  const [mappings, setMappings] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchMappings = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('square_product_mappings')
      .select(`
        *,
        odin_product:products(id, strain),
        odin_product_type:product_types(id, name)
      `)
      .eq('is_active', true)
      .order('square_item_name')

    if (error) {
      console.error('Failed to fetch product mappings:', error)
    } else {
      setMappings(data || [])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchMappings()
  }, [fetchMappings])

  const createMapping = useCallback(async (mapping) => {
    const { data, error } = await supabase
      .from('square_product_mappings')
      .insert({
        square_catalog_id: mapping.square_catalog_id,
        square_variation_id: mapping.square_variation_id,
        square_item_name: mapping.square_item_name,
        square_variation_name: mapping.square_variation_name,
        odin_product_id: mapping.odin_product_id,
        odin_product_type_id: mapping.odin_product_type_id,
        weight_per_unit_kg: mapping.weight_per_unit_kg
      })
      .select()
      .single()

    if (error) {
      toast.error('Failed to create mapping', { description: error.message })
      throw error
    }

    toast.success('Product mapping created')
    await fetchMappings()
    return data
  }, [fetchMappings])

  const deleteMapping = useCallback(async (id) => {
    const { error } = await supabase
      .from('square_product_mappings')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', id)

    if (error) {
      toast.error('Failed to remove mapping', { description: error.message })
      throw error
    }

    toast.success('Mapping removed')
    await fetchMappings()
  }, [fetchMappings])

  return { mappings, loading, createMapping, deleteMapping, refresh: fetchMappings }
}
